import { useState, useEffect, useCallback } from "react";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN } from "../api";

// Loads /requests (+ /requests/stats) and refreshes them when anything changes,
// so "needs attention" highlights clear the moment someone replies or quotes.
export default function useLiveRequests({ withStats = false } = {}) {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [, setTick] = useState(0); // re-render every minute so "waiting 12 min" stays current

  const reload = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/requests"),
        withStats ? api.get("/requests/stats") : Promise.resolve(null),
      ]);
      setRequests(Array.isArray(r.data) ? r.data : []);
      if (s) setStats(s.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || "Couldn't load requests.");
    } finally {
      setLoading(false);
    }
  }, [withStats]);

  useEffect(() => {
    reload();
    const poll = setInterval(reload, 60000);
    const clock = setInterval(() => setTick((t) => t + 1), 60000);
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(poll); clearInterval(clock); window.removeEventListener("focus", onFocus); };
  }, [reload]);

  useEffect(() => {
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"], auth: { token: localStorage.getItem("token") } });
    socket.emit("join", "admin");
    let t;
    const soon = () => { clearTimeout(t); t = setTimeout(reload, 400); };
    ["request:new", "request:update", "quote:update", "conversation:update", "request:trash"].forEach((ev) => socket.on(ev, soon));
    return () => { clearTimeout(t); socket.disconnect(); };
  }, [reload]);

  return { requests, stats, loading, error, reload };
}
